/**
 * FP3-044 Phase A — Admin Profile tab.
 *
 * Editable form for the admin-owned student fields. Uses the new
 * /api/admin/students/{id} PATCH which enforces a strict allowlist
 * server-side.
 *
 * FP3-044B Issue 1 — Eligibility chips replaced with the existing
 * `EligibilityEditor` so up to 4 entries can be selected
 * independently, designation logic (primary / secondary / additional)
 * is handled centrally, and SLD/DD/PSD subcategory pickers appear
 * automatically when those categories are selected.
 *
 * FP3-044B Issue 2 — DOB / IEP due / MET due use the new
 * `DateField` (text input + calendar popover with year dropdown)
 * instead of the bare native `<input type="date">`.
 */
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import api from "@/lib/api";
import EligibilityEditor from "@/components/student/EligibilityEditor";
import DateField from "./DateField";
import {
  ATTENDANCE_OPTIONS,
  SEX_OPTIONS,
  SERVICE_STATUS_OPTIONS,
  LEGACY_STATUS_LABELS,
  normalizeAttendancePattern,
  normalizeSex,
} from "@/lib/studentProfileTaxonomy";
// FP3-097 — SHARED teacher name/email helpers (same module as
// Provider Service Demographics): email→name suggestion + surname↔
// email match with an identical "Save anyway" confirmation.
import {
  deriveTeacherDisplay,
  teacherEmailMatches,
  TEACHER_MISMATCH_MESSAGE,
} from "@/lib/teacherIdentity";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

// FP3-092 — option lists come from the SHARED taxonomy module (same
// values as Provider Service Demographics). Legacy stored tokens
// (half_day_am, M/F/NB, …) normalize on seed and persist canonically
// on the next save.

export default function AdminProfileTab({ studentId, student, onSaved }) {
  const [draft, setDraft] = useState(() => seedDraft(student));
  // FP3-099 — saved baseline: the last server state this form
  // accepted. A field is dirty when draft differs from baseline.
  const baselineRef = useRef(seedDraft(student));
  const prevStudentIdRef = useRef(studentId);
  const [schools, setSchools] = useState([]);
  const [eligibilityTaxonomy, setEligibilityTaxonomy] = useState([]);
  const [lreLevels, setLreLevels] = useState([]);
  const [saving, setSaving] = useState(false);
  // FP3-097 — Teacher name ↔ email mismatch confirmation. Identical
  // behavior to Provider Service Demographics: warn inline, require
  // an explicit "Save anyway" when the changed pair doesn't match.
  const [pendingTeacherMismatch, setPendingTeacherMismatch] = useState(false);
  const teacherPairMismatch =
    !!((draft.teacher || "").trim() && (draft.teacher_email || "").trim()) &&
    !teacherEmailMatches(draft.teacher, draft.teacher_email);
  const teacherPairChanged =
    (draft.teacher || "") !== (student.teacher || "") ||
    (draft.teacher_email || "") !== (student.teacher_email || "");

  // FP3-099 — draft lifecycle. Full reinitialization happens ONLY
  // when the studentId changes (or after a confirmed save, handled in
  // doSubmit). A refreshed `student` object for the SAME student —
  // focus/visibility background refetch, sibling-tab save — merges
  // refreshed values into UNTOUCHED fields only; dirty fields are
  // never overwritten silently.
  useEffect(() => {
    const seed = seedDraft(student);
    if (prevStudentIdRef.current !== studentId) {
      prevStudentIdRef.current = studentId;
      baselineRef.current = seed;
      setDraft(seed);
      return;
    }
    const base = baselineRef.current;
    const dirty = dirtyFieldsOf(draft, base);
    if (dirty.length === 0) {
      baselineRef.current = seed;
      setDraft(seed);
      return;
    }
    const merged = { ...seed };
    const conflicted = [];
    dirty.forEach((f) => {
      merged[f] = draft[f];
      // Same field changed remotely AND differs from the local edit →
      // keep the local draft and surface a calm notice.
      if (!fieldEqual(seed, base, f) && !fieldEqual(seed, draft, f)) {
        conflicted.push(FIELD_NOTICE_LABELS[f] || f);
      }
    });
    baselineRef.current = seed;
    setDraft(merged);
    if (conflicted.length > 0) {
      toast.message("This profile changed elsewhere while you were editing", {
        description: `Kept your unsaved ${conflicted.join(", ")} — review before saving.`,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [student, studentId]);

  useEffect(() => {
    api.listSchools()
      .then((r) => {
        const rows = Array.isArray(r?.data) ? r.data : [];
        setSchools(
          rows
            .filter((s) => s && s.id && (s.name || "").trim())
            .sort((a, b) => (a.name || "").localeCompare(b.name || "")),
        );
      })
      .catch(() => setSchools([]));
    api.getEligibilityCategories()
      .then((r) => setEligibilityTaxonomy(r.data?.categories || []))
      .catch(() => setEligibilityTaxonomy([]));
    api.getLreLevels()
      .then((r) => setLreLevels(r.data?.levels || []))
      .catch(() => setLreLevels([]));
  }, []);

  const submit = async () => {
    // FP3-097 — mismatch gate: a changed, non-matching pair requires
    // an explicit "Save anyway" (legitimate aliases stay saveable).
    if (teacherPairMismatch && teacherPairChanged) {
      setPendingTeacherMismatch(true);
      return;
    }
    await doSubmit();
  };

  const doSubmit = async () => {
    setPendingTeacherMismatch(false);
    setSaving(true);
    try {
      const payload = {};
      const fields = [
        // FP3-062 — Identity fields included so the diff picks up
        // renames; backend allowlist already includes both.
        "first_name", "last_name",
        "grade", "teacher", "teacher_email",
        "school_id", "campus", "attendance_pattern",
        "date_of_birth", "sex",
        "lre", "iep_due_date", "met_due_date", "status",
        // FP3-045 — Identity fields. Backend normalizes student_email
        // (lowercase + trim) and trims external_id.
        "external_id", "student_email",
      ];
      fields.forEach((f) => {
        if ((draft[f] || "") !== (student[f] || "")) payload[f] = draft[f];
      });
      // Eligibility categories — compare normalized entries.
      if (
        JSON.stringify(normalizeElig(draft.eligibility_categories || []))
        !== JSON.stringify(normalizeElig(student.eligibility_categories || []))
      ) {
        payload.eligibility_categories = (draft.eligibility_categories || []).filter(
          (e) => (e?.category || "").trim(),
        );
      }
      if (Object.keys(payload).length === 0) {
        toast.message("No changes to save");
        setSaving(false);
        return;
      }
      const r = await api.patchAdminStudent(studentId, payload);
      if (r.data?.ok) {
        // FP3-099 — the canonical student returned by the server is
        // the new baseline: dirty state clears, saved values stay on
        // screen without any refresh.
        const fresh = r.data.student;
        if (fresh) {
          const seed = seedDraft(fresh);
          baselineRef.current = seed;
          setDraft(seed);
        }
        toast.success("Profile updated");
        onSaved?.();
      } else {
        toast.error("Save did not confirm");
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message || "Save failed";
      toast.error(detail);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="p-6 bg-white border-[#E5E3DB]" data-testid="admin-profile-tab">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {/* FP3-062 — Editable student identity. Server-side PATCH
            (`/api/admin/students/{id}`) already accepts these
            fields; we surface them here so admin family + solo
            owners no longer have to drop into Mongo to rename a
            mis-imported student. */}
        <Field label="First name">
          <input
            data-testid="admin-profile-first-name"
            value={draft.first_name}
            onChange={(e) => setDraft((d) => ({ ...d, first_name: e.target.value }))}
            className={inputCls}
            placeholder="Required"
            maxLength={80}
          />
        </Field>
        <Field label="Last name">
          <input
            data-testid="admin-profile-last-name"
            value={draft.last_name}
            onChange={(e) => setDraft((d) => ({ ...d, last_name: e.target.value }))}
            className={inputCls}
            placeholder="Required"
            maxLength={80}
          />
        </Field>

        <Field label="Grade">
          <input
            data-testid="admin-profile-grade"
            value={draft.grade}
            onChange={(e) => setDraft((d) => ({ ...d, grade: e.target.value }))}
            className={inputCls}
          />
        </Field>
        <Field label="Status">
          <select
            data-testid="admin-profile-status"
            value={draft.status}
            onChange={(e) => setDraft((d) => ({ ...d, status: e.target.value }))}
            className={inputCls}
          >
            {/* Keep a legacy stored value (evaluation/exited) readable
                without forcing a silent rewrite on unrelated saves. */}
            {LEGACY_STATUS_LABELS[draft.status] && (
              <option value={draft.status}>{LEGACY_STATUS_LABELS[draft.status]}</option>
            )}
            {SERVICE_STATUS_OPTIONS.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </Field>

        <Field label="Teacher">
          <input
            data-testid="admin-profile-teacher"
            value={draft.teacher}
            onChange={(e) => setDraft((d) => ({ ...d, teacher: e.target.value }))}
            className={inputCls}
          />
        </Field>
        <Field label="Teacher email">
          <input
            data-testid="admin-profile-teacher-email"
            type="email"
            value={draft.teacher_email}
            onChange={(e) => {
              const email = e.target.value;
              setDraft((d) => {
                // FP3-097 — Same auto-suggest rule as Provider:
                // derive the teacher name from the email local part
                // when the name is blank or still equals the previous
                // derivation; never overwrite a manual entry.
                const prevDerived = deriveTeacherDisplay(d.teacher_email);
                const next = { ...d, teacher_email: email };
                if (!d.teacher || d.teacher === prevDerived) {
                  next.teacher = deriveTeacherDisplay(email);
                }
                return next;
              });
            }}
            className={inputCls}
          />
          {teacherPairMismatch && (
            <p
              className="text-[10px] text-[#B85C5C] mt-1"
              data-testid="admin-profile-teacher-mismatch"
            >
              {TEACHER_MISMATCH_MESSAGE}
            </p>
          )}
        </Field>

        {/* FP3-045 — Identity fields. Both optional; both kept inside
            the admin allowlist so a provider PATCH cannot mutate them
            through this surface. */}
        <Field label="External Student ID">
          <input
            data-testid="admin-profile-external-id"
            value={draft.external_id}
            onChange={(e) => setDraft((d) => ({ ...d, external_id: e.target.value }))}
            placeholder="e.g. SIS-12345"
            className={inputCls}
          />
        </Field>
        <Field label="Student Email">
          <input
            data-testid="admin-profile-student-email"
            type="email"
            value={draft.student_email}
            onChange={(e) => setDraft((d) => ({ ...d, student_email: e.target.value }))}
            placeholder="student@school.edu"
            className={inputCls}
          />
        </Field>

        <Field label="Campus">
          <select
            data-testid="admin-profile-campus"
            value={draft.school_id}
            onChange={(e) => {
              const sid = e.target.value;
              const match = schools.find((s) => s.id === sid);
              setDraft((d) => ({ ...d, school_id: sid, campus: match ? match.name : "" }));
            }}
            className={inputCls}
          >
            <option value="">— Select a campus —</option>
            {schools.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        </Field>
        <Field label="Attendance pattern">
          <select
            data-testid="admin-profile-attendance"
            value={draft.attendance_pattern}
            onChange={(e) => setDraft((d) => ({ ...d, attendance_pattern: e.target.value }))}
            className={inputCls}
          >
            {ATTENDANCE_OPTIONS.map((a) => (
              <option key={a.value} value={a.value}>{a.label}</option>
            ))}
          </select>
        </Field>

        <Field label="Date of birth">
          <DateField
            value={draft.date_of_birth}
            onChange={(iso) => setDraft((d) => ({ ...d, date_of_birth: iso }))}
            testid="admin-profile-dob"
            minYear={1990}
            maxYear={new Date().getFullYear()}
          />
        </Field>
        <Field label="Sex">
          <select
            data-testid="admin-profile-sex"
            value={draft.sex || ""}
            onChange={(e) => setDraft((d) => ({ ...d, sex: e.target.value }))}
            className={inputCls}
          >
            {SEX_OPTIONS.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </Field>

        <Field label="LRE">
          <select
            data-testid="admin-profile-lre"
            value={draft.lre || ""}
            onChange={(e) => setDraft((d) => ({ ...d, lre: e.target.value }))}
            className={inputCls}
          >
            <option value="">—</option>
            {/* FP3-092 — bind to the canonical `key` property (same
                as Provider Service Demographics), never `code`. */}
            {lreLevels.map((lvl) => (
              <option key={lvl.key} value={lvl.key}>{lvl.label}</option>
            ))}
          </select>
        </Field>

        <div /> {/* spacer */}

        <Field label="IEP due date">
          <DateField
            value={draft.iep_due_date}
            onChange={(iso) => setDraft((d) => ({ ...d, iep_due_date: iso }))}
            testid="admin-profile-iep-due"
          />
        </Field>
        <Field label="MET / evaluation due date">
          <DateField
            value={draft.met_due_date}
            onChange={(iso) => setDraft((d) => ({ ...d, met_due_date: iso }))}
            testid="admin-profile-met-due"
          />
        </Field>
      </div>

      {/* Eligibility — multi-entry editor with positional designation. */}
      <div className="mt-6" data-testid="admin-profile-eligibility">
        <p className="text-[11px] font-medium text-[#6B7280] mb-2">Eligibility categories</p>
        <EligibilityEditor
          value={draft.eligibility_categories || []}
          taxonomy={eligibilityTaxonomy}
          onChange={(next) => setDraft((d) => ({ ...d, eligibility_categories: next }))}
          testIdPrefix="admin-profile-elig"
        />
      </div>

      <div className="mt-6 flex justify-end">
        <button
          type="button"
          onClick={submit}
          disabled={saving}
          data-testid="admin-profile-save"
          className="px-4 py-2 rounded-md bg-[#1F2937] text-white text-[12px] font-medium hover:bg-black disabled:opacity-60"
        >
          {saving ? "Saving…" : "Save changes"}
        </button>
      </div>

      {/* FP3-097 — Teacher name ↔ email mismatch confirmation. Same
          behavior as Provider Service Demographics. */}
      <AlertDialog open={pendingTeacherMismatch} onOpenChange={setPendingTeacherMismatch}>
        <AlertDialogContent data-testid="admin-profile-teacher-mismatch-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Teacher name and email may not match</AlertDialogTitle>
            <AlertDialogDescription>
              {TEACHER_MISMATCH_MESSAGE} District aliases, maiden-name emails,
              initials-only addresses, and shared classroom inboxes are all
              valid — choose Save anyway if this pair is correct.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="admin-profile-teacher-mismatch-cancel"
              disabled={saving}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={doSubmit}
              disabled={saving}
              data-testid="admin-profile-teacher-mismatch-save-anyway"
              className="bg-[#1F2937] hover:bg-black text-white"
            >
              Save anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

function Field({ label, children }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[11px] font-medium text-[#6B7280]">{label}</span>
      {children}
    </label>
  );
}

const inputCls = "h-9 text-sm border border-[#d4d2cc] rounded-lg px-2 bg-white w-full";

function seedDraft(student) {
  return {
    // FP3-062 — Identity is editable in the same form as the
    // demographic fields. Backend PATCH already allowlists these.
    first_name: student.first_name || "",
    last_name: student.last_name || "",
    grade: student.grade || "",
    teacher: student.teacher || "",
    teacher_email: student.teacher_email || "",
    school_id: student.school_id || "",
    campus: student.campus || "",
    // FP3-092 — normalize legacy aliases (half_day_am → am, M → male)
    // so stored legacy tokens render correctly and persist canonically
    // on the next save.
    attendance_pattern: normalizeAttendancePattern(student.attendance_pattern) || "full_day",
    date_of_birth: student.date_of_birth || "",
    sex: normalizeSex(student.sex),
    eligibility_categories: Array.isArray(student.eligibility_categories)
      ? student.eligibility_categories
      : [],
    lre: student.lre || "",
    iep_due_date: student.iep_due_date || "",
    met_due_date: student.met_due_date || "",
    status: student.status || "active",
    // FP3-045 — Identity fields seed straight from the canonical doc.
    external_id: student.external_id || "",
    student_email: student.student_email || "",
  };
}

function normalizeElig(list) {
  return (list || [])
    .map((e) => ({
      category: e?.category || "",
      subcategories: Array.isArray(e?.subcategories) ? [...e.subcategories].sort() : [],
    }))
    .filter((e) => e.category);
}

// FP3-099 — dirty-field tracking. Field list mirrors seedDraft +
// the PATCH diff list; eligibility compares normalized entries.
const DRAFT_FIELDS = [
  "first_name", "last_name", "grade", "teacher", "teacher_email",
  "school_id", "campus", "attendance_pattern", "date_of_birth", "sex",
  "lre", "iep_due_date", "met_due_date", "status",
  "external_id", "student_email",
];

const FIELD_NOTICE_LABELS = {
  first_name: "First name",
  last_name: "Last name",
  grade: "Grade",
  teacher: "Teacher",
  teacher_email: "Teacher email",
  school_id: "Campus",
  campus: "Campus",
  attendance_pattern: "Attendance pattern",
  date_of_birth: "Date of birth",
  sex: "Sex",
  lre: "LRE",
  iep_due_date: "IEP due date",
  met_due_date: "MET due date",
  status: "Status",
  external_id: "External ID",
  student_email: "Student email",
  eligibility_categories: "Eligibility",
};

function fieldEqual(a, b, f) {
  if (f === "eligibility_categories") {
    return (
      JSON.stringify(normalizeElig(a.eligibility_categories || []))
      === JSON.stringify(normalizeElig(b.eligibility_categories || []))
    );
  }
  return (a[f] || "") === (b[f] || "");
}

function dirtyFieldsOf(draft, baseline) {
  return [...DRAFT_FIELDS, "eligibility_categories"].filter(
    (f) => !fieldEqual(draft, baseline, f),
  );
}
